import { Injectable } from '@angular/core';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

import { Env } from '../env/env';

declare global {
  interface Window {
    Pusher: typeof Pusher;
  }
}

/**
 * Client Reverb (laravel-echo) — canaux `private-ops.*`.
 *
 * Auth : POST {api}/broadcasting/auth avec le Bearer Sanctum (même token
 * que l'API, stocké par l'auth). Instancié paresseusement : tant qu'aucun
 * écran temps réel n'est ouvert, pas de connexion WS.
 */
@Injectable({ providedIn: 'root' })
export class EchoService {
  private echo: Echo<'reverb'> | null = null;

  connect(): Echo<'reverb'> {
    if (this.echo !== null) {
      return this.echo;
    }

    window.Pusher = Pusher;

    this.echo = new Echo({
      broadcaster: 'reverb',
      key: Env.reverb.appKey,
      wsHost: Env.reverb.host,
      wsPort: Env.reverb.port,
      wssPort: Env.reverb.port,
      forceTLS: Env.reverb.useTLS,
      // ⚠️ pusher-js : le transport s'appelle `ws` MÊME en TLS (c'est forceTLS
      // qui produit le wss://). `['wss']` seul = aucun transport valide →
      // pusher n'essaie RIEN, silencieusement (ni appel réseau, ni state_change).
      enabledTransports: ['ws', 'wss'],
      authEndpoint: `${Env.apiBaseUrl}/broadcasting/auth`,
      auth: {
        headers: {
          // TODO(auth): brancher sur l'AuthTokenStorage quand le login Sanctum arrive
          Authorization: `Bearer ${window.localStorage.getItem('gisgo-ops-token') ?? ''}`,
        },
      },
    });

    // Diagnostic ops : trace le cycle de vie de la connexion Reverb dans la
    // console (connecting → connected, ou unavailable/failed) + les erreurs.
    const scheme = Env.reverb.useTLS ? 'wss' : 'ws';
    const target = `${scheme}://${Env.reverb.host}:${Env.reverb.port}`;
    const pusher = (this.echo.connector as { pusher: Pusher }).pusher;
    pusher.connection.bind('state_change', (states: { previous: string; current: string }) => {
      console.info(`[reverb] ${states.previous} → ${states.current} (${target})`);
    });
    pusher.connection.bind('error', (error: unknown) => {
      console.error('[reverb] erreur de connexion', error);
    });

    return this.echo;
  }

  disconnect(): void {
    this.echo?.disconnect();
    this.echo = null;
  }
}
